// /store/actions.js
import { MatjrahAPI, MatjrahClient, isWorkspaceConflict } from "@/archive";

let activeApplicationRequest = null;
let activeApplicationId = null;

export default {
  counterUp({ state, commit }) {
    commit("setCounter", state.counter + 1);
  },
  updateStatusApplication({ commit }, payload) {
    commit("updateStatusApplication", payload);
  },
  setLocalStories({ commit }, payload) {
    commit("setLocalStories", payload);
  },
  async getShopData({ commit }) {
    commit("setCatalogStatus", "loading");
    commit("setCatalogError", null);

    const [categories, manufacturers] = await Promise.allSettled([
      MatjrahAPI.data.getCategories(),
      MatjrahAPI.data.getManufacturers(),
    ]);

    commit("setCategories", categories.status === "fulfilled" ? categories.value : []);
    commit("setManufacturers", manufacturers.status === "fulfilled" ? manufacturers.value : []);

    if (categories.status === "rejected" && manufacturers.status === "rejected") {
      commit("setCatalogStatus", "error");
      commit("setCatalogError", "catalog_unavailable");
    } else if (categories.status === "rejected" || manufacturers.status === "rejected") {
      commit("setCatalogStatus", "partial");
      commit("setCatalogError", "catalog_partially_available");
    } else {
      commit("setCatalogStatus", "ready");
    }

    return {
      categories: categories.status === "fulfilled" ? categories.value : [],
      manufacturers: manufacturers.status === "fulfilled" ? manufacturers.value : [],
    };
  },
  async getApplicationByID({ commit }, payload) {
    const requestId = payload.RequestId;
    MatjrahClient.currentAppID = requestId // Set App ID to use it in data requests

    if (!activeApplicationRequest || activeApplicationId !== requestId) {
      activeApplicationId = requestId;
      activeApplicationRequest = MatjrahAPI.app.getAppByID({
        app_id: requestId,
      }).finally(() => {
        activeApplicationRequest = null;
        activeApplicationId = null;
      });
    }

    const data = await activeApplicationRequest;
    commit("setApplication", data);
    return data;
  },
  /**
   * Saves the application.
   *
   * Every page in the studio holds its own copy of `draft_config`, taken when
   * that page loaded, and each save PUTs the whole thing back. Nothing told the
   * server which version the page had started from, so the last write simply
   * won: saving the design page shipped its stale `homeSections` and deleted
   * sections added on the homepage page minutes earlier, and for a published
   * app that goes straight to customers over the air.
   *
   * `config_hash` is the stamp the API returns for the `draft_config` this page
   * loaded, and it refreshes on every successful save. Echoing it back makes
   * `AppAPIRepository::update()` reject a write whose base has moved on, so a
   * stale save fails loudly instead of overwriting newer work.
   */
  async updateApplication({ commit, state }, payload) {
    const body = { ...((payload && payload.body) || {}) };
    const expectedConfigHash = state.application && state.application.config_hash;
    if (body.draft_config && expectedConfigHash && !body.expected_config_hash) {
      body.expected_config_hash = expectedConfigHash;
    }

    try {
      const data = await MatjrahAPI.app.updateApp({ ...payload, body });

      commit("setApplication", data);
      return data;
    } catch (error) {
      if (!isWorkspaceConflict(error)) throw error;

      // The rejected copy is the one still on screen. Pull the configuration
      // that actually won so the page reloads from it, rather than leaving the
      // merchant to retry the same stale save.
      const current = await MatjrahAPI.app.getAppByID({
        app_id: payload.app_id,
      });
      commit("setApplication", current);

      const conflict = new Error(
        "تم تعديل التطبيق من مكان آخر. تم تحديث البيانات، راجع الصفحة ثم احفظ من جديد."
      );
      conflict.workspaceConflict = true;
      throw conflict;
    }
  },

  async createApplication({ commit }, payload) {
    const data = await MatjrahAPI.app.createApp(payload);

    commit("setApplication", data);
    return data;
  },
};
